/**
 * A table with real markup (`scope`, `aria-sort`), optional sortable headers and optional row windowing for long lists
 * (UX-A11Y-06, UX-PERF-04). Windowed tables use a fixed row height, spacer rows and `aria-rowcount` / `aria-rowindex`.
 */
import type { ComponentChildren, JSX } from 'preact';
import { ROW_H, useWindow } from '../ui.tsx';

export interface Column<T> {
	id: string;
	header: ComponentChildren;
	render: (row: T, index: number) => ComponentChildren;
	/** Right-aligned tabular figures. */
	num?: boolean;
	mono?: boolean;
	/** Makes the header a sort button. */
	sortable?: boolean;
	/** Row header cell (`th scope="row"`). */
	rowHeader?: boolean;
	className?: string;
}

export type SortState = { id: string; dir: 'asc' | 'desc' } | null;

export function DataTable<T>({
	caption,
	columns,
	rows,
	rowKey,
	virtual = false,
	maxHeight = 420,
	sort,
	onSort,
	rowClass,
	testId,
	windowRef,
}: {
	caption: string;
	columns: Column<T>[];
	rows: readonly T[];
	rowKey: (row: T, index: number) => string | number;
	virtual?: boolean;
	maxHeight?: number;
	sort?: SortState;
	onSort?: (id: string) => void;
	rowClass?: (row: T) => string | undefined;
	testId?: string;
	/** Receives the windowing controls (scroll to a row). */
	windowRef?: (w: { scrollToRow: (i: number) => void }) => void;
}): JSX.Element {
	const win = useWindow(rows.length, ROW_H);
	if (virtual) windowRef?.({ scrollToRow: win.scrollToRow });
	const start = virtual ? win.start : 0;
	const end = virtual ? win.end : rows.length;
	const head = (
		<thead>
			<tr>
				{columns.map((c) => {
					const active = sort && sort.id === c.id;
					return (
						<th
							key={c.id}
							scope="col"
							class={c.num ? 'num' : undefined}
							aria-sort={
								c.sortable
									? active
										? sort?.dir === 'asc'
											? 'ascending'
											: 'descending'
										: 'none'
									: undefined
							}
						>
							{c.sortable && onSort ? (
								<button type="button" class="th-sort" onClick={() => onSort(c.id)}>
									{c.header}
									<span aria-hidden="true">
										{active ? (sort?.dir === 'asc' ? ' ▲' : ' ▼') : ''}
									</span>
								</button>
							) : (
								c.header
							)}
						</th>
					);
				})}
			</tr>
		</thead>
	);
	const body = (
		<tbody>
			{virtual && win.before > 0 ? (
				<tr class="spacer" ref={(el) => el?.style.setProperty('height', `${win.before}px`)}>
					<td colSpan={columns.length} aria-hidden="true" />
				</tr>
			) : null}
			{rows.slice(start, end).map((row, k) => {
				const i = start + k;
				return (
					<tr
						key={rowKey(row, i)}
						class={rowClass?.(row)}
						aria-rowindex={virtual ? i + 2 : undefined}
					>
						{columns.map((c) => {
							const cls =
								[c.num ? 'num' : '', c.mono ? 'mono' : '', c.className ?? '']
									.filter(Boolean)
									.join(' ') || undefined;
							return c.rowHeader ? (
								<th key={c.id} scope="row" class={cls}>
									{c.render(row, i)}
								</th>
							) : (
								<td key={c.id} class={cls}>
									{c.render(row, i)}
								</td>
							);
						})}
					</tr>
				);
			})}
			{virtual && win.after > 0 ? (
				<tr class="spacer" ref={(el) => el?.style.setProperty('height', `${win.after}px`)}>
					<td colSpan={columns.length} aria-hidden="true" />
				</tr>
			) : null}
		</tbody>
	);
	return (
		<div
			class="tablewrap"
			ref={virtual ? win.ref : undefined}
			data-testid={testId}
			style={virtual ? { maxHeight: `${maxHeight}px`, overflow: 'auto' } : undefined}
			// a scrollable region must be reachable by keyboard (WCAG 2.1.1)
			tabIndex={virtual ? 0 : undefined}
			role={virtual ? 'region' : undefined}
			aria-label={virtual ? caption : undefined}
		>
			<table
				aria-rowcount={virtual ? rows.length + 1 : undefined}
				class={virtual ? 'is-virtual' : undefined}
			>
				<caption class="visually-hidden">{caption}</caption>
				{head}
				{body}
			</table>
		</div>
	);
}
